import { is } from 'immutable';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import classNames from 'classnames';
import GoogleMap from 'react-google-maps/lib/components/GoogleMap';
import Select from 'react-select';
import { clientDb } from '../../../shared/firebase';
import { setShowedDevices } from '../../../shared/helpers';
import { withSentry } from '../../../shared/hoc/withSentry';
import { iFullStoreState, iDeviceDetails, iList, iTag } from '../../../shared/interfaces';
import { localStorage } from '../../../shared/storage';
import { useRedux } from '../../../states/redux-state';
import { DashboardSize } from '../../../stores/reducers/dashboardInfo';
import { DevicesDetailsContainer } from "../../../stores/reducers/devicesData";
import { selectTags } from '../../../stores/reducers/general-selectors';
import { compareByNameAscending, compareByNameDescending } from '../../../utils/sorting';
import DashboardBlock from '../../DashboardBlock';
import ModalHeaderComponent from '../../menus/modal/modal-header';
import { sortBySelectStyles, getOptionColors } from '../../general/selections-grid/constants';
import styles from './device-page.module.scss';
import NewDeviceItem from "./DeviceItem";
import DeviceListItem from "./DeviceListItem";
import { useTbs } from './hooks/useTbs';
import { Button, ButtonStyles } from '../../Button';
import { Actions as ReportActions } from '../../../stores/reducers/report-reducers';
import { BATTERY_ISSUE_PING_MSGS } from '../../../shared/constants';

interface CustomOptionState {
  data: {
    kind: 'sort' | 'filter';
    value: string;
  };
  selectProps: {
    currentSortValue: string;
    currentTypeFilter: string;
  };
}

enum ViewMode {
  GRID = 'grid',
  LIST = 'list',
}

const DEVICES_LIST_PREFS_KEY = 'devicesPage:listPrefs:v1';

const readDevicesListPrefs = () => {
  try {
    const raw = window.sessionStorage.getItem(DEVICES_LIST_PREFS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

const writeDevicesListPrefs = (prefs) => {
  try {
    window.sessionStorage.setItem(DEVICES_LIST_PREFS_KEY, JSON.stringify(prefs));
  } catch (e) {}
};

const DevicesPage = () => {
  const history = useHistory();
  type DeviceTypeFilter = 'all' | 'tracked' | 'inventory' | 'archived' | 'tracked_inventory' | 'inactive_3wks' | 'tbs' | 'battery_issue';
  type SortValue = 'asc' | 'desc' | 'last_report_asc' | 'last_report_desc';
  type DeviceSelectOption = { value: SortValue | DeviceTypeFilter; label: string; kind: 'sort' | 'filter' };

  const selectOptions: { label: string; options: DeviceSelectOption[] }[] = [
    {
      label: 'Filter',
      options: [
        { value: 'all', label: 'All Vehicles', kind: 'filter' },
        { value: 'tracked', label: 'Tracked Only', kind: 'filter' },
        { value: 'inventory', label: 'Not Tracked', kind: 'filter' },
        { value: 'tracked_inventory', label: 'Tracked & Not Tracked', kind: 'filter' },
        { value: 'inactive_3wks', label: '3 wks +', kind: 'filter' },
        { value: 'tbs', label: 'To Be Sold', kind: 'filter' },
        { value: 'battery_issue', label: 'Battery Issue', kind: 'filter' },
        { value: 'archived', label: 'Archived', kind: 'filter' },
      ],
    },
    {
      label: 'Sort',
      options: [
        { value: 'asc', label: 'Vehicle Name ASC', kind: 'sort' },
        { value: 'desc', label: 'Vehicle Name DESC', kind: 'sort' },
        { value: 'last_report_asc', label: 'Last Reported ASC', kind: 'sort' },
        { value: 'last_report_desc', label: 'Last Reported DESC', kind: 'sort' },
      ],
    },
  ];

  const findFilterOption = (value: DeviceTypeFilter) => {
    for (let i = 0; i < selectOptions.length; i++) {
      const group = selectOptions[i];
      for (let j = 0; j < group.options.length; j++) {
        const opt = group.options[j];
        if (opt.kind === 'filter' && opt.value === value) return opt;
      }
    }
    return selectOptions[0].options[0];
  };

  const isValidTypeFilter = (value: any): value is DeviceTypeFilter => {
    return value === 'all'
      || value === 'tracked'
      || value === 'inventory'
      || value === 'archived'
      || value === 'tracked_inventory'
      || value === 'inactive_3wks'
      || value === 'tbs'
      || value === 'battery_issue';
  };

  const isValidSortValue = (value: any): value is SortValue => {
    return value === 'asc'
      || value === 'desc'
      || value === 'last_report_asc'
      || value === 'last_report_desc';
  };

  const prefs = readDevicesListPrefs();
  const initialTypeFilter: DeviceTypeFilter = isValidTypeFilter(prefs.typeFilter) ? prefs.typeFilter : 'tracked_inventory';
  const initialSortValue: SortValue = isValidSortValue(prefs.sortValue) ? prefs.sortValue : 'asc';

  const [sortValue, setSortValue] = useState<SortValue>(initialSortValue);
  const [typeFilter, setTypeFilter] = useState<DeviceTypeFilter>(initialTypeFilter);
  const [lastSelected, setLastSelected] = useState<DeviceSelectOption>(findFilterOption(initialTypeFilter));
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);

  const handleHeaderChange = (selectedOption: DeviceSelectOption) => {
    setLastSelected(selectedOption);
    if (selectedOption.kind === 'sort') {
      setSortValue(selectedOption.value as SortValue);
    } else {
      setTypeFilter(selectedOption.value as DeviceTypeFilter);
      setSortValue('asc');
    }
  };

  useEffect(() => {
    writeDevicesListPrefs({ typeFilter, sortValue });
  }, [typeFilter, sortValue]);

  const devices = useSelector<iFullStoreState, DevicesDetailsContainer>(
    (s) => s.devicesData.devicesDetails,
    (l, r) => is(l, r)
  );

  const devicesArray = devices.valueSeq().toArray();

  const devicesLastPingsRaw = useRedux((s) => s.devicesData.devicesLastPing);
  const tags = useSelector((s: iFullStoreState) => selectTags(s)) as iList<iTag>;

  const pageTitle = process.env.REACT_APP_DEVICES_TAB_NAME || 'Devices';


  const compareByLastReport = (a, b) => {
    const aLastPing = devicesLastPingsRaw.get(a.id);
    const bLastPing = devicesLastPingsRaw.get(b.id);
    const aTime = aLastPing?.time?.unix() ?? null;
    const bTime = bLastPing?.time?.unix() ?? null;

    if (sortValue === 'last_report_desc') {
      if (aTime !== null && bTime !== null) return aTime - bTime;
    } else {
      if (aTime !== null && bTime !== null) return bTime - aTime;
    }

    if (aTime !== null) return -1;
    if (bTime !== null) return 1;
    return 0;
  };


  const isInventoryOnly = (device: iDeviceDetails) => Boolean(device.isInventoryOnlyDevice);
  const isArchived = (device: iDeviceDetails) => Boolean(device.isArchived);
  const hasPing = (device: iDeviceDetails) => !isArchived(device) && !isInventoryOnly(device);
  const allIds = devicesArray.map(d => d.id);
  const { tbsMap } = useTbs(allIds);

  const filteredDevicesArray = devicesArray.filter((device: iDeviceDetails) => {
    switch (typeFilter) {
      case 'all':
        return true;
      case 'archived':
        return isArchived(device);
      case 'tracked':
        return hasPing(device);
      case 'inventory':
        return isInventoryOnly(device);
      case 'tracked_inventory':
        return !isArchived(device);
      case 'inactive_3wks': {
        if (isArchived(device)) return false;
        const lastPing = devicesLastPingsRaw.get(device.id);
        return lastPing?.time && moment().diff(moment(lastPing.time), 'weeks') >= 3
      }
      case 'tbs': {
        if (isArchived(device)) return false;
        return tbsMap ? tbsMap[device.id] : false;
      }
      case 'battery_issue': {
        if (isArchived(device)) return false;
        const lastPing = devicesLastPingsRaw.get(device.id);
        if (!lastPing) return false;

        const pingMsg = lastPing?.msg ?? 'N/A';
        return BATTERY_ISSUE_PING_MSGS.includes(pingMsg as typeof BATTERY_ISSUE_PING_MSGS[number]);
      }
      default:
        return true;
    }
  });

  const tagFilteredDevicesArray = selectedTagId
    ? filteredDevicesArray.filter((device: iDeviceDetails) => {
        const tag = tags[selectedTagId];
        const tagDeviceIds = tag?.instances?.device ?? {};
        return Boolean(tagDeviceIds[device.id]);
      })
    : filteredDevicesArray;

  const sortArray = () => {
    switch (sortValue) {
      case 'desc':
        return tagFilteredDevicesArray.sort(compareByNameDescending).map(({ id }) => id);
      case 'asc':
        return tagFilteredDevicesArray.sort(compareByNameAscending).map(({ id }) => id);
      case 'last_report_asc':
      case 'last_report_desc':
        return tagFilteredDevicesArray.sort(compareByLastReport).map(({ id }) => id);
      default:
        return tagFilteredDevicesArray.map(({ id }) => id);
    }
  };

  const sortedArray = sortArray();

  const dispatch = useDispatch();

  const devicesLastPings = devicesLastPingsRaw
    .valueSeq()
    .toArray();

  const mapRef = useSelector<
    iFullStoreState,
    React.RefObject<GoogleMap> | null
  >((state) => state.gmap.mapRef());

  let isRenderedMap = useSelector<iFullStoreState, boolean>(
    (state) => state.gmap.isRendered
  );

  const devicesMarker = useRedux((s) => s.devicesData.devicesLastPing)
    .valueSeq()
    .toArray()
    .filter((it) => it !== null);

  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.LIST);

  const user = localStorage.get('login-init-user');

  useEffect(() => {
    let markerBounds = new google.maps.LatLngBounds();
    devicesMarker.forEach((marker) => {
      markerBounds.extend(
        new google.maps.LatLng(
          marker.coordinates.location.lat,
          marker.coordinates.location.lng
        )
      );
    });
    mapRef?.current?.fitBounds(markerBounds);

    const fetchViewMode = async () => {
      const userFirebaseViewMode = (await clientDb().child(`people/people-details/${user.uid}/viewMode`).once('value')).val();
      if (userFirebaseViewMode) {
        setViewMode(userFirebaseViewMode);
      }
    };

    fetchViewMode();
  }, []);

  useEffect(() => {
    if (!isRenderedMap) {
      let markerBounds = new google.maps.LatLngBounds();
      devicesMarker.forEach((marker) => {
        markerBounds.extend(
          new google.maps.LatLng(
            marker.coordinates.location.lat,
            marker.coordinates.location.lng
          )
        );
      });
      mapRef?.current?.fitBounds(markerBounds);
    }
    setShowedDevices(devicesLastPings, dispatch);
  }, [devicesLastPings, isRenderedMap]);

  const size = useSelector<iFullStoreState, DashboardSize>(
    (s) => s.dashboardInfo.size
  );

  const isNotFullSizeDashboard = !(size === DashboardSize.OPEN_FULL);

  const dashboardNotBig = isNotFullSizeDashboard && size !== DashboardSize.OPEN;

  const deviceSortStyles = {
    ...sortBySelectStyles,
    option: (provided, state) => {
      const { data, selectProps } = state as CustomOptionState;
      const isActiveSort = data.kind === 'sort' && data.value === selectProps.currentSortValue;
      const isActiveFilter = data.kind === 'filter' && data.value === selectProps.currentTypeFilter;
      const isActive = isActiveSort || isActiveFilter;

      return {
        ...(sortBySelectStyles.option ? sortBySelectStyles.option(provided, state) : provided),
        ...getOptionColors(state, isActive),
      };
    },
    control: (provided, state) => ({
      ...sortBySelectStyles.control(provided, state),
      width: '52px',
      padding: '0px',
      minHeight: '16px',
      height: '16px',
    }),
    valueContainer: (provided) => ({
      ...sortBySelectStyles.valueContainer(provided),
      justifyContent: 'center',
      width: '100%',
      padding: '0px',
      lineHeight: '8px',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 'fit-content',
    }),
    menu: (provided) => ({
      ...sortBySelectStyles.menu(provided),
      overflowY: 'visible',
    }),
  };

  const runInventoryReport = () => {
    dispatch(ReportActions.RESET_ALL_FILTERS());

    sortedArray.forEach(deviceId => {
        dispatch(ReportActions.TOGGLE_ITEM_FILTER('device', deviceId));
    });

    history.push('/reports/new/inventory');
};

  const tagsArray = Object.values(tags ?? {});
  type TagSelectOption = { value: string | null; label: string };
  const tagOptions: TagSelectOption[] = [
    { value: null, label: 'All Tags' },
    ...tagsArray
      .filter((tag) => !tag.details.isHidden)
      .map((tag) => ({ value: tag.details.id, label: tag.details.name })),
  ];

  const selectedTagOption = tagOptions.find((option) => option.value === selectedTagId) || tagOptions[0];

  const tagFilterStyles = {
    ...sortBySelectStyles,
    option: (provided, state) => ({
      ...sortBySelectStyles.option(provided, state),
      padding: '0 0 0 6px',
    }),
    control: (provided, state) => ({
      ...sortBySelectStyles.control(provided, state),
      width: '52px',
      padding: '0px',
      minHeight: '16px',
      height: '16px',
    }),
    valueContainer: (provided) => ({
      ...sortBySelectStyles.valueContainer(provided),
      justifyContent: 'center',
      width: '100%',
      padding: '0px',
      lineHeight: '8px',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 'fit-content',
    }),
    menu: (provided) => ({
      ...sortBySelectStyles.menu(provided),
      overflowY: 'visible',
    }),
  };

  const tagSelect = (
    <Select
      options={tagOptions}
      value={selectedTagOption}
      onChange={(option: TagSelectOption) => setSelectedTagId(option?.value ?? null)}
      styles={tagFilterStyles}
      isSearchable={false}
      menuPlacement="auto"
      filterLabel="Tag"
      components={{
        SingleValue: (props: any) => {
          const isOpen = props.selectProps.menuIsOpen;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <span style={{ fontSize: '8px', lineHeight: '8px', color: '#111' }}>{props.selectProps.filterLabel}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                style={{
                  width: 10,
                  height: 10,
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </div>
          );
        },
        DropdownIndicator: () => null,
      }}
    />
  );

  return (
    <>
    <DashboardBlock>
        <ModalHeaderComponent
          title={pageTitle}
          showSelect={true}
          options={selectOptions}
          option={lastSelected}
          handleChange={handleHeaderChange}
          dashboardSize={dashboardNotBig}
          selectStyles={deviceSortStyles}
          selectExtraProps={{ currentSortValue: sortValue, currentTypeFilter: typeFilter }}
          secondSelectComponent={tagsArray.length > 0 ? tagSelect : undefined}
        />
      <div className={viewMode === ViewMode.GRID ? styles.deviceGrid : styles.deviceList}>
        {sortedArray.map((deviceId) => {
          return viewMode === ViewMode.GRID ? (
            <Link
              key={deviceId}
              className={classNames({ [styles.showFullCardWidthOnSmallScreensOn1505pxScreen]: isNotFullSizeDashboard })}
              to={{pathname: `/device/${deviceId}`, state: {sortedDevices: sortedArray}}}
            >
            <NewDeviceItem
              deviceId={deviceId}
            />
          </Link>) : (
            <Link key={deviceId} to={{pathname: `/device/${deviceId}`, state: {sortedDevices: sortedArray}}}>
              <DeviceListItem deviceId={deviceId} />
            </Link>
          )
        })}
      </div>
      {sortedArray.length > 0 && (
        <div style={{textAlign: 'center', marginTop: "auto" }}>
            <Button
                styleType={ButtonStyles.BLACK_WHITE}
                onClick={runInventoryReport}
                style={{ width: '100%', height: "30px" }}
            >
              Inventory Report
            </Button>
        </div>
      )}
    </DashboardBlock>
    </>
  );
};

export default withSentry(DevicesPage);


// todo: move this to shared path
export const formatWhenLast = (time?: moment.Moment) => {
  if (!time) return false;

  const now = moment();

  const lastActiveDays = now.diff(time, 'days');
  const lastActiveHours = Math.floor(now.diff(time, 'hours'));
  const lastActiveMinutes = now.diff(time, 'minutes');

  let ago = '';

  if (lastActiveDays)
    ago = `${lastActiveDays} Day${lastActiveDays === 1 ? '' : 's'} ago`;
  else if (lastActiveHours)
    ago = `${lastActiveHours} Hour${lastActiveHours === 1 ? '' : 's'} ago`;
  else if (lastActiveMinutes)
    ago = `${lastActiveMinutes} Minute${lastActiveMinutes === 1 ? '' : 's'}`;

  return ago;
};